import { xpToday } from '@/data/daily';
import { getSupabase } from '@/data/supabase';
import { readNotificationPrefs, reminderLine, type NotificationPrefs } from '@/lib/notifications';
import { isNative, platformName } from './platform';

/**
 * Reminders on a phone (plan docs 13 and 17).
 *
 * A browser tab can only notify while it is open, so on the web the reminders
 * are best-effort. A native shell can hand them to the OS: the next week of
 * daily nudges is scheduled as local notifications, and re-planned every time
 * the app opens or a session ends — which is how "only if you have not played
 * today" is kept, since the OS cannot evaluate that itself. Today's reminder
 * is simply not scheduled once there has been a session.
 *
 * Push (league results, the server's reminders) needs a token; registering
 * stores it in `push_tokens`, and the `reminders` function does the sending.
 */

/** Ids are ranges so ours can be cancelled without touching anyone else's. */
export const DAILY_REMINDER_ID = 1000;
export const STREAK_REMINDER_ID = 2000;
const OUR_ID_CEILING = 3000;
/** How far ahead the OS holds reminders; re-planned long before it runs out. */
export const PLAN_DAYS = 7;

export interface PlannedReminder {
  id: number;
  title: string;
  body: string;
  at: Date;
}

export interface ReminderPlanInput {
  prefs: NotificationPrefs;
  reminderHour: number | null;
  streak: number;
  playedToday: boolean;
  now: Date;
}

/**
 * The reminders to hand to the OS. Pure.
 *
 * Daily nudges for the coming week, each with its own line so they do not
 * repeat; the streak warning for tonight only, because tomorrow's streak
 * depends on today. Nothing is planned for today once there has been a
 * session, and nothing in the past.
 */
export function plannedReminders(input: ReminderPlanInput): PlannedReminder[] {
  const { prefs, reminderHour, streak, playedToday, now } = input;
  const out: PlannedReminder[] = [];

  for (let day = 0; day < PLAN_DAYS; day += 1) {
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + day);
    const dayIndex = Math.floor(date.getTime() / 86_400_000);
    const today = day === 0;

    if (prefs.dailyReminder && reminderHour !== null) {
      const at = new Date(date);
      at.setHours(reminderHour, 0, 0, 0);
      if (!(today && (playedToday || at.getTime() <= now.getTime()))) {
        out.push({
          id: DAILY_REMINDER_ID + day,
          title: 'Audigoal',
          body: reminderLine(dayIndex),
          at,
        });
      }
    }

    if (today && prefs.streakAtRisk && streak >= 3) {
      const at = new Date(date);
      at.setHours(21, 0, 0, 0);
      if (!playedToday && at.getTime() > now.getTime()) {
        out.push({
          id: STREAK_REMINDER_ID,
          title: `🔥 ${streak}-day streak on the line`,
          body: 'One quick lesson keeps it alive.',
          at,
        });
      }
    }
  }

  return out;
}

export type NativePermission = 'granted' | 'denied' | 'unavailable';

/** What the OS currently allows, without asking. */
export async function checkNativeNotifications(): Promise<NativePermission | 'prompt'> {
  if (!isNative()) return 'unavailable';
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    const status = await LocalNotifications.checkPermissions();
    if (status.display === 'granted') return 'granted';
    if (status.display === 'denied') return 'denied';
    return 'prompt';
  } catch {
    return 'unavailable';
  }
}

/**
 * Re-plans the OS reminders from the current preferences and today's play.
 * The one entry point the app calls; everything else here is its parts.
 */
export async function replanReminders(
  profile: { reminderHour: number | null; streakCurrent: number },
  now = new Date(),
): Promise<void> {
  if (!isNative()) return;
  const xp = await xpToday(now);
  await syncNativeReminders({
    prefs: readNotificationPrefs(),
    reminderHour: profile.reminderHour,
    streak: profile.streakCurrent,
    playedToday: xp > 0,
    now,
  });
}

/** Asks the OS. Native only; the web asks the browser through `lib/notifications`. */
export async function requestNativeNotifications(): Promise<NativePermission> {
  if (!isNative()) return 'unavailable';
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    const status = await LocalNotifications.requestPermissions();
    return status.display === 'granted' ? 'granted' : 'denied';
  } catch {
    return 'unavailable';
  }
}

/**
 * Replaces our pending local notifications with the current plan.
 *
 * Cancel-then-schedule rather than diffing: the plan is at most eight rows,
 * and a diff that drifted would leave a stale reminder firing after the
 * learner had turned it off.
 */
export async function syncNativeReminders(input: ReminderPlanInput): Promise<void> {
  if (!isNative()) return;
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    const permission = await LocalNotifications.checkPermissions();
    if (permission.display !== 'granted') return;

    const pending = await LocalNotifications.getPending();
    const ours = pending.notifications
      .filter((n) => n.id >= DAILY_REMINDER_ID && n.id < OUR_ID_CEILING)
      .map((n) => ({ id: n.id }));
    if (ours.length > 0) await LocalNotifications.cancel({ notifications: ours });

    const planned = plannedReminders(input);
    if (planned.length === 0) return;
    await LocalNotifications.schedule({
      notifications: planned.map((reminder) => ({
        id: reminder.id,
        title: reminder.title,
        body: reminder.body,
        schedule: { at: reminder.at, allowWhileIdle: true },
        smallIcon: 'ic_stat_audigoal',
      })),
    });
  } catch {
    // A reminder that cannot be scheduled is a reminder that does not happen.
  }
}

export type PushOutcome = 'registered' | 'denied' | 'unavailable';

/**
 * Registers for push and stores the token against the account.
 *
 * Only ever called after a learner turns a notification category on: the OS
 * permission dialog is the same one local notifications use, and asking on
 * launch is the fastest way to a permanent "no".
 */
export async function registerPushToken(userId: string): Promise<PushOutcome> {
  if (!isNative()) return 'unavailable';
  const supabase = await getSupabase();
  if (supabase === null) return 'unavailable';
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const status = await PushNotifications.requestPermissions();
    if (status.receive !== 'granted') return 'denied';

    return await new Promise<PushOutcome>((resolve) => {
      void PushNotifications.addListener('registration', ({ value }) => {
        void supabase
          .from('push_tokens')
          .upsert({ user_id: userId, token: value, platform: platformName() })
          .then(({ error }) => resolve(error === null ? 'registered' : 'unavailable'));
      });
      void PushNotifications.addListener('registrationError', () => resolve('denied'));
      void PushNotifications.register();
    });
  } catch {
    return 'unavailable';
  }
}
